import { motion } from 'framer-motion';
import { useLanguage } from '@/lib/index';
import { IMAGES } from '@/assets/images';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

// ─── Per-language UI strings ───────────────────────────────────────────────
const uiStrings: Record<string, {
  badge: string;
  title: string;
  subtitle: string;
  breadcrumb: string;
  expertise: string;
  comingSoon: string;
}> = {
  'zh-CN': {
    badge: '顾问委员会',
    title: '战略顾问委员会',
    subtitle: '汇聚全球顶尖专家学者，为 SHONCHIN MANOR & CO. 提供战略洞见与跨领域智慧。',
    breadcrumb: '首页',
    expertise: '专业领域',
    comingSoon: '更多顾问成员即将加入',
  },
  'zh-TW': {
    badge: '顧問委員會',
    title: '戰略顧問委員會',
    subtitle: '匯聚全球頂尖專家學者，為 SHONCHIN MANOR & CO. 提供戰略洞見與跨領域智慧。',
    breadcrumb: '首頁',
    expertise: '專業領域',
    comingSoon: '更多顧問成員即將加入',
  },
  en: {
    badge: 'Advisory Board',
    title: 'Strategic Advisory Board',
    subtitle: 'A distinguished council of global experts and thought leaders providing strategic insight and cross-disciplinary wisdom to SHONCHIN MANOR & CO.',
    breadcrumb: 'Home',
    expertise: 'Areas of Expertise',
    comingSoon: 'More members coming soon',
  },
  ja: {
    badge: '諮問委員会',
    title: '戦略諮問委員会',
    subtitle: 'SHONCHIN MANOR & CO. に戦略的洞察と学際的知見を提供する、世界トップクラスの専門家集団。',
    breadcrumb: 'ホーム',
    expertise: '専門分野',
    comingSoon: '今後、新たなメンバーが順次加入予定です',
  },
  ko: {
    badge: '자문위원회',
    title: '전략 자문위원회',
    subtitle: 'SHONCHIN MANOR & CO.에 전략적 통찰과 다학제적 지혜를 제공하는 세계적 전문가 그룹.',
    breadcrumb: '홈',
    expertise: '전문 분야',
    comingSoon: '추가 자문위원이 곧 합류할 예정입니다',
  },
};

// ─── Advisor data per language ──────────────────────────────────────────────
const advisorData: Record<string, {
  id: string;
  name: string;
  photo: string;
  photoPosition?: string;
  title: string;
  bio: string[];
  expertise: string[];
}[]> = {
  'zh-CN': [
    {
      id: 'william-liao',
      name: 'William Liao',
      photo: IMAGES.WILLIAM,
      photoPosition: '50% 15%',
      title: '太平洋风险投资（PVC）管理合伙人',
      bio: [
        'William Liao 拥有逾30年横跨投资银行、风险投资及私募股权的从业经验。',
        '现任太平洋风险投资（PVC）管理合伙人。PVC 为其于2014年联合创立的5亿美元成长型资本基金，总部设于香港，专注投资高速成长的颠覆性行业，历史投资组合涵盖阿里巴巴、Formula E Holdings 及 Rani Therapeutics 等知名企业。',
        '廖先生职业生涯起步于伦敦普华永道（PwC），担任特许会计师，其后加入高盛国际（Goldman Sachs International）任职副总裁。2001年回国后，先后出任美林证券（Merrill Lynch）上海首席代表及副总裁。',
        '廖先生毕业于英国白金汉大学，并取得英格兰及威尔士特许会计师公会（ICAEW）特许会计师资格。',
      ],
      expertise: [
        '风险投资与私募股权',
        '投资银行',
        '普华永道 / 高盛 / 美林',
        '阿里巴巴 & Formula E 投资人',
        '香港资本市场',
        'ICAEW 特许会计师',
      ],
    },
    {
      id: 'katherine-choi',
      name: 'Katherine Choi',
      photo: IMAGES.KATHERINE,
      photoPosition: '50% 10%',
      title: '韩国国家品牌振兴院院长',
      bio: [
        'Katherine Choi 现任韩国国家品牌振兴院院长。该院经韩国文化体育观光部批准设立，致力于开展全球国家品牌状况研究、培育未来领导人才并向政府提供政策建议，每年发布全球60个国家的国家品牌指数。',
        '自2015年起，韩国国家品牌振兴院每年在韩国国会举办国家品牌颁奖典礼，表彰为提升韩国国家品牌做出突出贡献的企业、文化人士、艺术家及体育人物。',
        'Choi 教授同时担任首尔综合科技大学（aSSIST）教授及主任，并曾在长江商学院EMBA双学位项目中任教。',
        '2022年，她主导发起全球首个网红MBA（iMBA），为网红群体提供系统化、专业化教育，并与全球各大平台及企业建立广泛的产学合作关系。她还担任首尔颁奖典礼的认证评审，该典礼由首尔经济振兴院主办，旨在认定优质韩国产品并助力其开拓全球市场。',
      ],
      expertise: [
        '国家品牌战略',
        '全球品牌研究',
        '网红教育（iMBA）',
        '政策咨询',
        '韩中商业',
        '首尔颁奖典礼评审',
      ],
    },
    {
      id: 'allen',
      name: 'Allen Wang',
      photo: IMAGES.ALLEN,
      photoPosition: '50% 20%',
      title: '华纳兄弟探索（中国）副总裁兼大中华区消费品业务负责人',
      bio: [
        'Allen 是一位在大中华区消费品授权及娱乐领域深耕逾20年的顶尖商界领袖，现任华纳兄弟探索中国区副总裁兼消费品业务负责人，曾先后在迪士尼、环球影业及拳头游戏担任高级管理职务。',
        '其专业能力覆盖影视、音乐、电竞及现场娱乐等完整娱乐产业价值链。在迪士尼，他管理的业务板块占中国消费品总营收的68%，监管逾200家授权商；在环球影业，他领导一支30人的跨职能团队，统筹业务拓展、品牌营销及知识产权保护。',
        'Allen 还在数字领域展现出卓越的开拓能力，率先为拳头游戏在天猫与抖音开辟直营电商渠道，充分展示了对传统零售与新媒体双渠道的深度掌控。',
        '他持有福德汉姆大学（纽约）金融MBA学位，能流利运用英语、普通话、粤语及闽南话。现任上海交通大学文化创意产业研究院USC-SJTU IP产业顾问，并担任苏州大学商学院发展基金委员会主席。',
      ],
      expertise: [
        '华纳兄弟探索副总裁',
        '消费品授权',
        '大中华区娱乐',
        'IP战略与保护',
        '迪士尼 / 环球 / 拳头游戏',
        '数字电商',
      ],
    },
  ],
  'zh-TW': [
    {
      id: 'william-liao',
      name: 'William Liao',
      photo: IMAGES.WILLIAM,
      photoPosition: '50% 15%',
      title: '太平洋風險投資（PVC）管理合夥人',
      bio: [
        'William Liao 擁有逾30年橫跨投資銀行、風險投資及私募股權的從業經驗。',
        '現任太平洋風險投資（PVC）管理合夥人。PVC 為其於2014年聯合創立的5億美元成長型資本基金，總部設於香港，專注投資高速成長的顛覆性行業，歷史投資組合涵蓋阿里巴巴、Formula E Holdings 及 Rani Therapeutics 等知名企業。',
        '廖先生職業生涯起步於倫敦普華永道（PwC），擔任特許會計師，其後加入高盛國際（Goldman Sachs International）任職副總裁。2001年返港後，先後出任美林證券（Merrill Lynch）上海首席代表及副總裁。',
        '廖先生畢業於英國白金漢大學，並取得英格蘭及威爾斯特許會計師公會（ICAEW）特許會計師資格。',
      ],
      expertise: [
        '風險投資與私募股權',
        '投資銀行',
        '普華永道 / 高盛 / 美林',
        '阿里巴巴 & Formula E 投資人',
        '香港資本市場',
        'ICAEW 特許會計師',
      ],
    },
    {
      id: 'katherine-choi',
      name: 'Katherine Choi',
      photo: IMAGES.KATHERINE,
      photoPosition: '50% 10%',
      title: '韓國國家品牌振興院院長',
      bio: [
        'Katherine Choi 現任韓國國家品牌振興院院長。該院經韓國文化體育觀光部批准設立，致力於開展全球國家品牌狀況研究、培育未來領導人才並向政府提供政策建議，每年發布全球60個國家的國家品牌指數。',
        '自2015年起，韓國國家品牌振興院每年在韓國國會舉辦國家品牌頒獎典禮，表彰為提升韓國國家品牌做出突出貢獻的企業、文化人士、藝術家及體育人物。',
        'Choi 教授同時擔任首爾綜合科技大學（aSSIST）教授及主任，並曾在長江商學院EMBA雙學位項目中任教。',
        '2022年，她主導發起全球首個網紅MBA（iMBA），為網紅群體提供系統化、專業化教育，並與全球各大平台及企業建立廣泛的產學合作關係。她還擔任首爾頒獎典禮的認證評審，該典禮由首爾經濟振興院主辦，旨在認定優質韓國產品並助力其開拓全球市場。',
      ],
      expertise: [
        '國家品牌戰略',
        '全球品牌研究',
        '網紅教育（iMBA）',
        '政策諮詢',
        '韓中商業',
        '首爾頒獎典禮評審',
      ],
    },
    {
      id: 'allen',
      name: 'Allen Wang',
      photo: IMAGES.ALLEN,
      photoPosition: '50% 20%',
      title: '華納兄弟探索（中國）副總裁兼大中華區消費品業務負責人',
      bio: [
        'Allen 是一位在大中華區消費品授權及娛樂領域深耕逾20年的頂尖商界領袖，現任華納兄弟探索中國區副總裁兼消費品業務負責人，曾先後在迪士尼、環球影業及拳頭遊戲擔任高級管理職務。',
        '其專業能力覆蓋影視、音樂、電競及現場娛樂等完整娛樂產業價值鏈。在迪士尼，他管理的業務板塊佔中國消費品總營收的68%，監管逾200家授權商；在環球影業，他領導一支30人的跨職能團隊，統籌業務拓展、品牌行銷及知識產權保護。',
        'Allen 還在數位領域展現出卓越的開拓能力，率先為拳頭遊戲在天貓與抖音開闢直營電商渠道，充分展示了對傳統零售與新媒體雙渠道的深度掌控。',
        '他持有福德漢姆大學（紐約）金融MBA學位，能流利運用英語、普通話、粵語及閩南話。現任上海交通大學文化創意產業研究院USC-SJTU IP產業顧問，並擔任蘇州大學商學院發展基金委員會主席。',
      ],
      expertise: [
        '華納兄弟探索副總裁',
        '消費品授權',
        '大中華區娛樂',
        'IP戰略與保護',
        '迪士尼 / 環球 / 拳頭遊戲',
        '數位電商',
      ],
    },
  ],
  en: [
    {
      id: 'william-liao',
      name: 'William Liao',
      photo: IMAGES.WILLIAM,
      photoPosition: '50% 15%',
      title: 'Managing Partner, Pacific Venture Capital (PVC)',
      bio: [
        'William Liao has over 30 years of working experience across investment banking, venture capital, and private equity investment.',
        'He is currently Managing Partner of Pacific Venture Capital (PVC), a US$500 million growth capital fund which he co-founded in 2014. Headquartered in Hong Kong, PVC focuses on investing in fast-growing and disruptive industries, with past investments including Alibaba, Formula E Holdings, and Rani Therapeutics.',
        'Mr. Liao began his career as a chartered accountant with PricewaterhouseCoopers (PwC) in London, before joining Goldman Sachs International as an Associate. In 2001, he returned to China, joining Merrill Lynch as Vice President and Chief Representative for Shanghai.',
        'He holds a bachelor\'s degree from the University of Buckingham (UK) and is a qualified accountant of the Institute of Chartered Accountants in England and Wales (ICAEW).',
      ],
      expertise: [
        'Venture Capital & PE',
        'Investment Banking',
        'PwC / Goldman Sachs / Merrill Lynch',
        'Alibaba & Formula E Investor',
        'Hong Kong Capital Markets',
        'ICAEW Chartered Accountant',
      ],
    },
    {
      id: 'katherine-choi',
      name: 'Katherine Choi',
      photo: IMAGES.KATHERINE,
      photoPosition: '50% 10%',
      title: 'President, Korean Institute of Nation Brand Promotion',
      bio: [
        'Katherine Choi is the President of the Korean Institute of Nation Brand Promotion, an institute established with the approval of the Ministry of Culture, Sports, and Tourism of the Republic of Korea.',
        'A distinguished brand expert, she conducts global research on national brand conditions, educates future leaders, and provides policy recommendations. Every year, she announces the National Brand Index for 60 countries worldwide.',
        'Since 2015, the Korean Institute of Nation Brand Promotion has organized the National Brand Awards Ceremony at the National Assembly of the Republic of Korea, selecting and honoring companies, cultural leaders, artists, and sports figures who have contributed to enhancing the Korean national brand.',
        'Katherine Choi also serves as a professor and director at the Seoul School of Integrated Sciences & Technologies (aSSIST). She has also taught at the Cheung Kong Graduate School of Business (CKGSB) as part of its EMBA Double Degree Program.',
        'In 2022, she led the launch of the world\'s first Influencer MBA (iMBA) at aSSIST, offering systematic and professional education for influencers. The program has cultivated a vibrant influencer community and established extensive industry–academic collaborations with leading platforms and companies worldwide.',
        'She also works in collaboration with the Seoul Business Agency as a certification judge for the Seoul Awards — an official certification program that recognizes outstanding Korean products and supports their global expansion.',
      ],
      expertise: [
        'Nation Brand Strategy',
        'Global Brand Research',
        'Influencer Education (iMBA)',
        'Policy Advisory',
        'Korea–China Business',
        'Seoul Awards Judge',
      ],
    },
    {
      id: 'allen',
      name: 'Allen Wang',
      photo: IMAGES.ALLEN,
      photoPosition: '50% 20%',
      title: 'VP & Head of Consumer Products China, Warner Bros. Discovery',
      bio: [
        'Allen is a top-tier business leader with over 20 years of deep expertise in consumer products licensing and entertainment across Greater China. He currently serves as Vice President and Head of Consumer Products China at Warner Bros. Discovery, and has previously held senior executive roles at Disney, Universal Pictures, and Riot Games.',
        'His professional capabilities span the full entertainment value chain — including film, television, music, esports, and live entertainment. At Disney, he managed a business portfolio representing 68% of total China consumer products revenue, overseeing more than 200 licensees. At Universal Pictures, he led a 30-person cross-functional team covering business development, brand marketing, and IP protection.',
        'Allen has also demonstrated strong digital acumen, pioneering direct e-commerce operations for Riot Games on Tmall and Douyin, showcasing mastery of both traditional retail and new media channels.',
        'He holds an MBA in Finance from Fordham University (New York) and is fluent in English, Mandarin, Cantonese, and Hokkien. He currently serves as an IP Industry Advisor at the USC-SJTU Institute of Cultural and Creative Industry at Shanghai Jiao Tong University, and as Chairman of the Development Fund of the Business Administration Department at Soochow University.',
      ],
      expertise: [
        'Warner Bros. Discovery VP',
        'Consumer Products & Licensing',
        'Greater China Entertainment',
        'IP Strategy & Protection',
        'Disney / Universal / Riot Games',
        'Digital & E-commerce',
      ],
    },
  ],
  ja: [
    {
      id: 'william-liao',
      name: 'William Liao',
      photo: IMAGES.WILLIAM,
      photoPosition: '50% 15%',
      title: 'パシフィック・ベンチャー・キャピタル（PVC）マネージング・パートナー',
      bio: [
        'William Liaoは、投資銀行、ベンチャーキャピタル、プライベートエクイティにわたる30年以上の豊富な経験を持ちます。',
        '現在は、自身が2014年に共同設立した5億米ドル規模の成長資本ファンド、パシフィック・ベンチャー・キャピタル（PVC）のマネージング・パートナーを務めています。香港を拠点とするPVCは、急成長中の革新的産業への投資を専門とし、アリババ、フォーミュラE・ホールディングス、ラニ・セラピューティクスなどへの投資実績があります。',
        '廖氏はロンドンのプライスウォーターハウスクーパース（PwC）で公認会計士としてキャリアをスタートし、その後ゴールドマン・サックス・インターナショナルにアソシエイトとして参加。2001年に中国へ帰国後、メリルリンチ上海の副社長兼首席代表を歴任しました。',
        '英国バッキンガム大学卒業、イングランドおよびウェールズ勅許会計士協会（ICAEW）認定の公認会計士資格を保有しています。',
      ],
      expertise: [
        'ベンチャーキャピタル & PE',
        '投資銀行',
        'PwC / ゴールドマン・サックス / メリルリンチ',
        'アリババ & フォーミュラE 投資家',
        '香港資本市場',
        'ICAEW 公認会計士',
      ],
    },
    {
      id: 'katherine-choi',
      name: 'Katherine Choi',
      photo: IMAGES.KATHERINE,
      photoPosition: '50% 10%',
      title: '韓国国家ブランド振興院 院長',
      bio: [
        'Katherine Choiは、韓国文化体育観光部の認可を受けて設立された韓国国家ブランド振興院の院長を務めています。',
        '著名なブランド専門家として、国家ブランドの状況に関する世界的な調査を行い、次世代リーダーの育成と政策提言に取り組んでいます。毎年、世界60か国の国家ブランド指数を発表しています。',
        '2015年以降、韓国国家ブランド振興院は韓国国会にて国家ブランド授賞式を開催し、韓国の国家ブランド向上に貢献した企業・文化人・芸術家・スポーツ選手を表彰しています。',
        'Choi教授はソウル総合科学技術大学院（aSSIST）の教授兼ディレクターも兼任し、長江商学院のEMBAダブルディグリープログラムでも教鞭をとっています。',
        '2022年、aSSISTにて世界初のインフルエンサーMBA（iMBA）を立ち上げ、インフルエンサー向けの体系的・専門的教育を提供するとともに、世界の主要プラットフォームや企業との産学連携を構築しました。また、ソウル経済振興院と協力し、優れた韓国製品を認定してグローバル展開を支援する「ソウルアワード」の認定審査員も務めています。',
      ],
      expertise: [
        '国家ブランド戦略',
        'グローバルブランド調査',
        'インフルエンサー教育（iMBA）',
        '政策アドバイザリー',
        '日韓・韓中ビジネス',
        'ソウルアワード審査員',
      ],
    },
    {
      id: 'allen',
      name: 'Allen Wang',
      photo: IMAGES.ALLEN,
      photoPosition: '50% 20%',
      title: 'ワーナー・ブラザース・ディスカバリー 中国コンシューマープロダクツ担当副社長',
      bio: [
        'Allenは、グレーターチャイナのコンシューマープロダクツ・ライセンシングおよびエンターテインメント分野で20年以上の深い専門知識を持つトップクラスのビジネスリーダーです。現在はワーナー・ブラザース・ディスカバリーの中国コンシューマープロダクツ担当副社長を務め、過去にはディズニー、ユニバーサル・ピクチャーズ、ライアットゲームズで上級幹部職を歴任しました。',
        '映画・テレビ・音楽・eスポーツ・ライブエンターテインメントなど、エンターテインメント産業のバリューチェーン全体をカバーする専門能力を有しています。ディズニーでは中国コンシューマープロダクツ総収益の68%を占める事業ポートフォリオを管理し、200社以上のライセンシーを統括。ユニバーサル・ピクチャーズでは、事業開発・ブランドマーケティング・IP保護を担う30名のクロスファンクショナルチームをリードしました。',
        'Allenはデジタル分野においても卓越した先見性を発揮し、天猫（Tmall）および抖音（Douyin）でライアットゲームズの直販eコマース事業を先駆的に立ち上げ、従来型小売と新メディアの両チャネルに精通していることを示しました。',
        'フォーダム大学（ニューヨーク）ファイナンスMBAを取得。英語・北京語・広東語・福建語に堪能。現在、上海交通大学USC-SJTU文化創造産業研究院のIPインダストリー・アドバイザー、および蘇州大学商学院発展基金委員会会長を務めています。',
      ],
      expertise: [
        'ワーナー・ブラザース・ディスカバリー副社長',
        'コンシューマープロダクツ & ライセンシング',
        'グレーターチャイナ・エンターテインメント',
        'IP戦略 & 保護',
        'ディズニー / ユニバーサル / ライアットゲームズ',
        'デジタル & eコマース',
      ],
    },
  ],
  ko: [
    {
      id: 'william-liao',
      name: 'William Liao',
      photo: IMAGES.WILLIAM,
      photoPosition: '50% 15%',
      title: '퍼시픽 벤처 캐피탈(PVC) 매니징 파트너',
      bio: [
        'William Liao는 투자은행, 벤처캐피탈, 사모펀드 분야에서 30년 이상의 풍부한 경험을 보유하고 있습니다.',
        '현재 2014년 공동 창립한 5억 달러 규모의 성장 자본 펀드인 퍼시픽 벤처 캐피탈(PVC)의 매니징 파트너를 맡고 있습니다. 홍콩에 본사를 둔 PVC는 고성장 파괴적 혁신 산업에 집중 투자하며, 알리바바, 포뮬러E 홀딩스, 라니 테라퓨틱스 등에 투자한 이력이 있습니다.',
        '랴오 씨는 런던 프라이스워터하우스쿠퍼스(PwC)에서 공인회계사로 커리어를 시작한 후 골드만삭스 인터내셔널에 합류했으며, 2001년 중국으로 귀국해 메릴린치 상하이 수석 대표 겸 부사장을 역임했습니다.',
        '영국 버킹엄 대학교 학사 출신으로, 잉글랜드·웨일스 공인회계사협회(ICAEW) 공인회계사 자격을 보유하고 있습니다.',
      ],
      expertise: [
        '벤처캐피탈 & 사모펀드',
        '투자은행',
        'PwC / 골드만삭스 / 메릴린치',
        '알리바바 & 포뮬러E 투자자',
        '홍콩 자본시장',
        'ICAEW 공인회계사',
      ],
    },
    {
      id: 'katherine-choi',
      name: 'Katherine Choi',
      photo: IMAGES.KATHERINE,
      photoPosition: '50% 10%',
      title: '한국 국가브랜드진흥원 원장',
      bio: [
        'Katherine Choi는 대한민국 문화체육관광부의 승인을 받아 설립된 한국 국가브랜드진흥원의 원장입니다.',
        '저명한 브랜드 전문가로서 국가 브랜드 현황에 관한 글로벌 연구를 수행하고, 미래 리더를 양성하며 정책 자문을 제공합니다. 매년 전 세계 60개국의 국가브랜드지수를 발표하고 있습니다.',
        '2015년부터 한국 국가브랜드진흥원은 대한민국 국회에서 국가브랜드 시상식을 개최하여 한국 국가 브랜드 향상에 기여한 기업, 문화인, 예술가, 스포츠인을 선정·시상하고 있습니다.',
        'Choi 교수는 서울과학종합대학원(aSSIST) 교수 겸 원장도 겸임하며, 장강경영대학원(CKGSB) EMBA 복수학위 프로그램에서도 강의했습니다.',
        '2022년에는 aSSIST에서 세계 최초의 인플루언서 MBA(iMBA)를 출범시켜 인플루언서를 위한 체계적·전문적 교육을 제공하고 세계 유수 플랫폼 및 기업과의 산학 협력을 구축했습니다. 또한 서울시 서울경제진흥원과 협력하여 우수 한국 제품을 인증하고 글로벌 진출을 지원하는 서울 어워즈의 인증 심사위원으로 활동하고 있습니다.',
      ],
      expertise: [
        '국가브랜드 전략',
        '글로벌 브랜드 연구',
        '인플루언서 교육(iMBA)',
        '정책 자문',
        '한중 비즈니스',
        '서울 어워즈 심사위원',
      ],
    },
    {
      id: 'allen',
      name: 'Allen Wang',
      photo: IMAGES.ALLEN,
      photoPosition: '50% 20%',
      title: '워너브라더스 디스커버리 중국 소비재 담당 부사장',
      bio: [
        'Allen은 그레이터 차이나 소비재 라이선싱 및 엔터테인먼트 분야에서 20년 이상의 깊은 전문성을 갖춘 최고 수준의 비즈니스 리더입니다. 현재 워너브라더스 디스커버리 중국 소비재 부문 부사장을 맡고 있으며, 이전에는 디즈니, 유니버설 픽처스, 라이엇 게임즈에서 고위 임원직을 역임했습니다.',
        '영화·TV·음악·e스포츠·라이브 엔터테인먼트 등 엔터테인먼트 산업 전반의 가치 사슬을 아우르는 전문 역량을 보유하고 있습니다. 디즈니에서는 중국 소비재 총매출의 68%를 차지하는 사업 포트폴리오를 관리하며 200개 이상의 라이선시를 감독했고, 유니버설 픽처스에서는 사업 개발·브랜드 마케팅·IP 보호를 담당하는 30인 크로스펑셔널 팀을 이끌었습니다.',
        'Allen은 디지털 분야에서도 탁월한 선구자적 역량을 발휘하여 라이엇 게임즈의 티몰 및 더우인 직영 전자상거래 채널을 개척함으로써 전통 유통과 뉴미디어 양 채널에 대한 전문성을 입증했습니다.',
        '포덤 대학교(뉴욕) 금융 MBA를 취득했으며 영어·표준중국어·광동어·민남어에 능통합니다. 현재 상하이 교통대학 USC-SJTU 문화창의산업연구원 IP 산업 자문위원, 소주대학 경영학부 발전기금위원회 위원장을 맡고 있습니다.',
      ],
      expertise: [
        '워너브라더스 디스커버리 부사장',
        '소비재 라이선싱',
        '그레이터 차이나 엔터테인먼트',
        'IP 전략 및 보호',
        '디즈니 / 유니버설 / 라이엇 게임즈',
        '디지털 & 전자상거래',
      ],
    },
  ],
};

export default function AdvisoryBoard() {
  const { language } = useLanguage();

  const ui = uiStrings[language] ?? uiStrings['en'];
  const advisors = advisorData[language] ?? advisorData['en'];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative bg-charcoal text-white py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-white/50 mb-8"
          >
            <span>{ui.breadcrumb}</span>
            <span>/</span>
            <span className="text-white/80">{ui.badge}</span>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeUp}
              className="inline-block px-4 py-1.5 bg-primary/20 text-primary border border-primary/30 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            >
              {ui.badge}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
            >
              {ui.title}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-white/70 leading-relaxed">
              {ui.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Advisors Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-10"
          >
            {advisors.map((advisor) => (
              <motion.div
                key={advisor.id}
                variants={fadeUp}
                className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 hover:shadow-xl transition-all duration-300"
              >
                {/* Photo */}
                <div className="relative h-80 overflow-hidden bg-muted">
                  <img
                    src={advisor.photo}
                    alt={advisor.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ objectPosition: advisor.photoPosition ?? 'center top' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h2 className="text-xl font-bold text-white mb-1">{advisor.name}</h2>
                    <p className="text-primary text-sm font-medium leading-snug">{advisor.title}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Bio paragraphs */}
                  <div className="space-y-3 mb-6">
                    {advisor.bio.map((para, idx) => (
                      <p key={idx} className="text-sm text-muted-foreground leading-relaxed">
                        {para}
                      </p>
                    ))}
                  </div>

                  {/* Expertise */}
                  <div>
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                      {ui.expertise}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {advisor.expertise.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium border border-primary/20"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Placeholder card — "Coming Soon" */}
            <motion.div
              variants={fadeUp}
              className="border-2 border-dashed border-border rounded-2xl flex flex-col items-center justify-center p-10 min-h-[480px] text-center opacity-50"
            >
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <span className="text-2xl text-muted-foreground">+</span>
              </div>
              <p className="text-sm text-muted-foreground font-medium">
                {ui.comingSoon}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
